import React, { useRef, useState, useEffect } from "react";
import {
  MapContainer,
  FeatureGroup,
  ImageOverlay,
  Polygon,
} from "react-leaflet";
import MapEditor from "./MapEditor";
import ShopListPopup from "./ShopListPopup";
import FloorSelection from "./FloorSelection";
import ShopSearch from "./ShopSearch";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { useFloors, useShops } from "../helpers/queries";
import { useSelector } from "react-redux";

function Map() {
  const floorsData = useFloors({});
  const shopList = useShops({
    language: window.appSettings?.language.toLowerCase(),
    pageSize: 100,
  });

  const PageLoaded = ({ data, shopList }) => {
    // state variable to set correct floor in admin panel after adding a new shop
    const justAddedShopsFloor = useSelector((state) => state.addedFloor);

    const [selectedFloor, setSelectedFloor] = useState(
      justAddedShopsFloor === null
        ? data[0]
        : data.find((floor) => floor.id === justAddedShopsFloor)
    );

    // state variable to determine the moment when figures have been to the map
    const [done, setDone] = useState(false);
    const [map, setMap] = useState();
    const [drawnItems, setDrawnItems] = useState(
      selectedFloor.map_settings.coordinates !== null
        ? selectedFloor.map_settings.coordinates
        : []
    );
    const polygonRefs = useRef({});
    const imageRef = useRef();
    const [activeFloors, setActiveFloors] = useState([]);

    let bgImageW = 2600;
    let bgImageH = 1452;
    let maxZoom = 7;
    let customCenter = [-(bgImageH / maxZoom), bgImageW / maxZoom];
    let mapOptionDrag = true;
    let mapOptionScroll = false;

    const bounds = [
      [0, 0],
      [1452, 2600],
    ];

    const onCreate = (shopMap) => {
      const imageBounds = imageRef.current.getBounds();
      shopMap.fitBounds(imageBounds);
      shopMap.setMaxBounds(imageBounds);
    };

    // if shop id is provided through appSettings, set the active shop, else set it null
    const [activeShop, setActiveShop] = useState(
      window.appSettings !== undefined
        ? window.appSettings?.shop_id !== "false"
          ? shopList.find(
              (shop) => shop.id.toString() === window.appSettings?.shop_id
            )
          : null
        : null
    );

    const openOnHover = (item) => {
      polygonRefs.current[item].on("mouseover", function (e) {
        polygonRefs.current[item].openPopup();
      });
    };

    const redirectShopPage = (item) => {
      polygonRefs.current[item].on("click", function (e) {
        window.location.href =
          polygonRefs.current[item].options.children.props.item.shop_link;
      });
    };

    const enablePopupAutoclose = (item) => {
      polygonRefs.current[item].on("mouseout", function (e) {
        polygonRefs.current[item].closePopup();
      });
    };

    useEffect(() => {
      // opens active shop's popups upon search
      if (activeShop !== null) {
        const keys = Object.entries(polygonRefs.current);
        keys.forEach((key) => {
          if (activeShop.id === key[1]?.options.children.props.item.shop_id) {
            polygonRefs.current[key[0]].openPopup();
          }
        });
      }

      // enables popups on hover and redirects to shop page on polygon click
      if (done === true) {
        for (var key in polygonRefs.current) {
          if (polygonRefs.current.hasOwnProperty(key)) {
            if (polygonRefs.current[key] !== null) {
              openOnHover(key);
              if (window.appSettings !== undefined) {
                window.appSettings.frontend === "true" && redirectShopPage(key);
                window.appSettings.frontend === "true" &&
                  enablePopupAutoclose(key);
              }
            }
          }
        }
      }
    }, [activeShop, polygonRefs, selectedFloor, done]);

    useEffect(() => {
      // if active shop is set, set active floors and select correct current floor
      if (activeShop !== null) {
        const floorsFound = [];

        data.forEach((floor) => {
          floor.map_settings.coordinates?.forEach((shop) => {
            if (activeShop.id === shop.shop_id) {
              floorsFound.push(floor);
            }
          });
        });
        setActiveFloors(floorsFound);

        if (floorsFound.length !== 0) {
          setSelectedFloor(floorsFound[0]);
          setDrawnItems(
            floorsFound[0].map_settings.coordinates !== null
              ? floorsFound[0].map_settings.coordinates
              : []
          );
        }
      }
    }, [activeShop, data]);

    return (
      <>
        <ShopSearch
          setActiveShop={setActiveShop}
          data={data}
          polygonRefs={polygonRefs}
          map={map}
          shopList={shopList}
          selectedFloor={selectedFloor}
          setSelectedFloor={setSelectedFloor}
          drawnItems={drawnItems}
          setDrawnItems={setDrawnItems}
          setActiveFloors={setActiveFloors}
          activeShop={activeShop}
        />
        <FloorSelection
          data={data}
          selectedFloor={selectedFloor}
          setSelectedFloor={setSelectedFloor}
          setDrawnItems={setDrawnItems}
          activeFloors={activeFloors}
        />
        <MapContainer
          className={`cc-map ${
            window.appSettings?.frontend === "true" && "cc-map--front"
          }`}
          style={{ height: 800, width: "100%" }}
          whenCreated={(map) => {
            onCreate(map);
            setMap(map);
          }}
          drawControl={false}
          center={customCenter}
          position="center"
          dragging={mapOptionDrag}
          scrollWheelZoom={mapOptionScroll}
          minZoom={-2}
          maxZoom={2}
          zoom={-1}
          gestureHandling={true}
          crs={L.CRS.Simple}
          boundsOptions={{ padding: [50, 50] }}
          attributionControl={false}
          tap={false}
        >
          <FeatureGroup>
            {drawnItems !== null &&
              drawnItems.map((elem, index) => {
                return (
                  <React.Fragment key={elem.leaflet_obj_id}>
                    <Polygon
                      // whenCreated={console.log("created", activeShop)}
                      key={elem.leaflet_obj_id}
                      className={`cc-map__poly cc-map__poly${
                        elem?.shop_id
                          ? activeShop?.id
                            ? activeShop.id === elem.shop_id
                              ? "--active"
                              : ""
                            : ""
                          : "--unassigned"
                      }`}
                      map={map}
                      positions={elem.coords}
                      ref={(m) => {
                        // <--- add polygon refs to ref object here
                        polygonRefs.current[elem.leaflet_obj_id] = m;
                        if (index === drawnItems.length - 1 && !done) {
                          setDone(true);
                        }
                      }}
                    >
                      <ShopListPopup
                        floorId={selectedFloor.id}
                        item={elem}
                        shopList={shopList}
                      />
                    </Polygon>
                  </React.Fragment>
                );
              })}
            {window.appSettings?.frontend !== "true" && (
              <MapEditor
                selectedFloor={selectedFloor}
                polygonRefs={polygonRefs}
                refsReady={done}
                setDrawnItems={setDrawnItems}
                map={map}
                data={selectedFloor}
              />
            )}
          </FeatureGroup>
          <ImageOverlay
            ref={imageRef}
            url={selectedFloor.map_settings.image}
            bounds={bounds}
          />
        </MapContainer>
      </>
    );
  };

  return (
    <>
      {floorsData.isLoading ||
      floorsData.isFetching ||
      shopList.isLoading ||
      shopList.isFetching ? (
        <div style={{ height: 998 }}>Loading...</div>
      ) : floorsData.isError ? (
        floorsData.error.message
      ) : (
        <PageLoaded data={floorsData.data} shopList={shopList.data} />
      )}
    </>
  );
}

export default Map;
